import type {
  MCPServerConfigurationType,
  MCPToolConfigurationType,
} from "@app/lib/actions/mcp";
import { MCPServerConfigurationSchema } from "@app/lib/actions/mcp_schemas";
import type { AgentMCPActionWithOutputType } from "@app/types/actions";
import type {
  AgentMessageType,
  InlineActivityStep,
} from "@app/types/assistant/conversation";
import type { GenerativeUiPart } from "@app/types/assistant/generative_ui";
import type { MODEL_PROVIDER_IDS } from "@app/types/assistant/models/providers";
import { ORDERED_REASONING_EFFORTS } from "@app/types/assistant/models/reasoning";
import type { ModelIdType } from "@app/types/assistant/models/types";
import { DbModelIdSchema } from "@app/types/shared/model_id";
import { assertNever } from "@app/types/shared/utils/assert_never";
import { TagSchema, type TagType } from "@app/types/tag";
import { UserSchema } from "@app/types/user";
import { z } from "zod";

/**
 * Agent configuration
 */

export const GLOBAL_AGENT_STATUSES = [
  "active",
  "disabled_by_admin",
  "disabled_missing_datasource",
  "disabled_free_workspace",
] as const;
export type GlobalAgentStatus = (typeof GLOBAL_AGENT_STATUSES)[number];

/**
 * Agent statuses:
 * - "active" means the agent can be used directly
 * - "archived" means the agent was either deleted, or that there is a newer
 *   version
 * - "draft" is used for the "try" button in builder, when the agent is not yet
 *   fully created / updated
 * - "pending" is used when the agent builder is opened for a new agent, before
 *   it is saved for the first time (allows capturing sId early). It allows having
 *   a sId before creating the agent.
 */
export const AGENT_STATUSES = [
  "active",
  "archived",
  "draft",
  "pending",
] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

const AGENT_CONFIGURATION_STATUSES = [
  "active",
  "archived",
  "draft",
  "pending",
  "disabled_by_admin",
  "disabled_missing_datasource",
  "disabled_free_workspace",
] as const;
export const AgentConfigurationStatusSchema = z.enum(
  AGENT_CONFIGURATION_STATUSES
);
export type AgentConfigurationStatus = z.infer<
  typeof AgentConfigurationStatusSchema
>;

/**
 * Agent configuration scope
 * - 'global' scope are Memoair agents, not editable, inside-list for all, cannot be overriden
 * - 'visible' scope are published agents
 * - 'hidden' scope are unpuiblished agents, visible by editors only
 */
export const AGENT_CONFIGURATION_SCOPES = [
  "global",
  "visible",
  "hidden",
] as const;
export type AgentConfigurationScope =
  (typeof AGENT_CONFIGURATION_SCOPES)[number];

/**
 * Defines strategies for fetching agent configurations based on various
 * 'views':
 * - 'current_user': Retrieves agents created or edited by the current user.
 * - 'list': Retrieves all active agents accessible to the user
 * - 'all': All non-private agents (so combines workspace, published and global
 *   agents); used e.g. for non-user calls such as API
 * - 'published': Retrieves all published agents.
 * - 'global': Retrieves all agents exclusively with a 'global' scope.
 * - 'admin_internal': Grants access to all agents, including private ones.
 * - 'manage': Retrieves all agents for the manage agents view (same as list, but including disabled agents).
 * - 'archived': Retrieves all agents that are archived. Only available to super
 *   users. Intended strictly for internal use with necessary superuser or admin
 *   authorization.
 * - 'favorites': Retrieves all agents marked as favorites by the current user.
 */
// TODO(agent-discovery) remove workspace, published, global
export type AgentsGetViewType =
  | "current_user"
  | "list"
  | "all"
  | "published"
  | "global"
  | "admin_internal"
  | "manage"
  | "archived"
  | "favorites";

export type AgentRecentAuthors = readonly string[];

export const AGENT_REINFORCEMENT_MODES = ["auto", "on", "off"] as const;
export type AgentReinforcementMode = (typeof AGENT_REINFORCEMENT_MODES)[number];

// Where the agent's loop executes. "internal" runs the Temporal agent loop in this
// platform; "external" hands the turn to a bring-your-own runner (wave 9c). The
// conversation model is identical either way — only loop execution differs.
export const AGENT_RUNNERS = ["internal", "external"] as const;
export type AgentRunner = (typeof AGENT_RUNNERS)[number];

// Where an external agent's turn actually runs. "managed" is the Claude Agent SDK hosted here on
// the workspace's own credential; "connected" hands the turn to Claude Code on the web through a
// routine the member configured there, billed to their Claude subscription rather than to us;
// "local" hands it to the owner's own laptop through the paired CLI bridge, which is present
// only while that laptop is running it; "relay" is a user's Instinct, an identity that carries
// messages between WhatsApp and the room and never runs a turn of its own.
export const AGENT_EXTERNAL_EXECUTION_MODES = [
  "managed",
  "connected",
  "local",
  "relay",
] as const;
export type AgentExternalExecutionMode =
  (typeof AGENT_EXTERNAL_EXECUTION_MODES)[number];

export const AGENT_REPOSITORY_POLICIES = ["ask", "automatic"] as const;
export type AgentRepositoryPolicy = (typeof AGENT_REPOSITORY_POLICIES)[number];

// Which repositories a local target may be dispatched into, and whether the laptop asks first.
// Keys are absolute paths because the server refuses an unlisted repository before the bridge
// ever sees the assignment, and a refusal has to compare the path that would actually be run.
export const AgentRepositoryPolicyMapSchema = z.record(
  z.string(),
  z.enum(AGENT_REPOSITORY_POLICIES)
);
export type AgentRepositoryPolicyMap = z.infer<
  typeof AgentRepositoryPolicyMapSchema
>;

export function isAbsoluteRepositoryPath(path: string): boolean {
  return path.startsWith("/") && !path.includes("..");
}

export const localAgentOwnerOnlyMessage =
  "Only the person whose computer runs this agent can change its folder or runtime.";

export function isSameRepositoryPolicy(
  a: AgentRepositoryPolicyMap | null | undefined,
  b: AgentRepositoryPolicyMap | null | undefined
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const leftKeys = Object.keys(left);
  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every((key) => left[key] === right[key])
  );
}

// A runtime profile is a CLI login the member's computer detected (`claude`, `claude-japesh`,
// `codex`). Only these names leave the laptop; env values and paths never do.
export const RuntimeProfileIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9._-]{1,64}$/);
export const RuntimeProfileSummarySchema = z.object({
  id: RuntimeProfileIdSchema,
  runtime: RuntimeProfileIdSchema,
  label: z.string().max(80),
  verified: z.boolean(),
});
export type RuntimeProfileSummary = z.infer<typeof RuntimeProfileSummarySchema>;
export const RuntimeProfileSummariesSchema = z
  .array(RuntimeProfileSummarySchema)
  .max(20);

// Anthropic's routine fire endpoint is the only URL a connected agent may be pointed at: a
// connected turn posts an assignment token to it, so an arbitrary host would be an exfiltration
// target.
export const AGENT_EXTERNAL_ROUTINE_FIRE_URL_REGEX =
  /^https:\/\/api\.anthropic\.com\/v1\/claude_code\/routines\/trig_[A-Za-z0-9]+\/fire$/;

export const AgentUsageSchema = z.object({
  messageCount: z.number(),
  conversationCount: z.number(),
  userCount: z.number(),
  timePeriodSec: z.number(),
});

export type AgentUsageType = z.infer<typeof AgentUsageSchema>;

// ModelProviderIdSchema and ModelIdSchema are inlined here to avoid importing
// from models/models.ts and models/providers.ts which have io-ts side effects
// (ioTsEnum → uuid) that crash in the Temporal workflow sandbox.
export const AgentModelConfigurationSchema = z.object({
  providerId: z.custom<(typeof MODEL_PROVIDER_IDS)[number]>(
    (val) => typeof val === "string"
  ),
  modelId: z.custom<ModelIdType>((val) => typeof val === "string"),
  temperature: z.number(),
  reasoningEffort: z.enum(ORDERED_REASONING_EFFORTS).optional(),
  responseFormat: z.string().optional(),
  metaData: z.record(z.string(), z.unknown()).optional(),
});

export type AgentModelConfigurationType = z.infer<
  typeof AgentModelConfigurationSchema
>;

export type AgentFetchVariant = "light" | "full" | "extra_light";

export type GlobalAgentContext = {
  userMessageRank: number;
  sidekickIsNewAgentFromScratch?: boolean;
  // Pre-formatted text that the Memoair global agent should echo as its NOOP
  // static reply for this turn. Set by `getStaticReplyForUserMessage` when the
  // triggering user message is a system-posted bootstrap/status update that
  // carries its own rendered content.
  staticReply?: string;
};

export const LightAgentConfigurationSchema = z.object({
  id: DbModelIdSchema,
  versionCreatedAt: z.string().nullable(),
  sId: z.string(),
  version: z.number(),
  versionAuthorId: DbModelIdSchema.nullable(),
  instructions: z.string().nullable(),
  model: AgentModelConfigurationSchema,
  status: AgentConfigurationStatusSchema,
  scope: z.enum(AGENT_CONFIGURATION_SCOPES),
  userFavorite: z.boolean(),
  name: z.string(),
  displayName: z.string().nullable().optional(),
  description: z.string(),
  pictureUrl: z.string(),
  lastAuthors: z.array(z.string()).readonly().optional(),
  editors: z.array(UserSchema).optional(),
  usage: AgentUsageSchema.optional(),
  feedbacks: z.object({ up: z.number(), down: z.number() }).optional(),
  maxStepsPerRun: z.number(),
  tags: z.array(TagSchema),
  templateId: z.string().nullable(),
  visualizationEnabled: z.boolean().optional(),
  requestedGroupIds: z.array(z.array(z.string())),
  requestedSpaceIds: z.array(z.string()),
  reinforcement: z.enum(AGENT_REINFORCEMENT_MODES).optional(),
  runner: z.enum(AGENT_RUNNERS).optional(),
  externalExecutionMode: z.enum(AGENT_EXTERNAL_EXECUTION_MODES).optional(),
  externalRoutineFireUrl: z.string().nullable().optional(),
  externalRepositoryPolicy:
    AgentRepositoryPolicyMapSchema.nullable().optional(),
  externalRuntimeProfile: RuntimeProfileIdSchema.nullable().optional(),
  // Whether a routine token is vaulted for this agent. The credential id never leaves the
  // server and the token itself is never readable back, so the builder needs a flag to know
  // it does not have to ask for the token again.
  externalRoutineTokenSaved: z.boolean().optional(),
  lastReinforcementAnalysisAt: z.string().nullable().optional(),
  canRead: z.boolean(),
  canEdit: z.boolean(),
  omittedThinking: z.boolean().optional(),
});

/**
 * @swaggerschema AgentConfiguration (swagger_schemas.ts), PrivateLightAgentConfiguration (swagger_private_schemas.ts)
 */
export type LightAgentConfigurationType = z.infer<
  typeof LightAgentConfigurationSchema
>;

export function agentDisplayName(agent: {
  name: string;
  displayName?: string | null;
}): string {
  return agent.displayName ?? agent.name;
}

export function isRelayAgent(agent: {
  runner?: string;
  externalExecutionMode?: string | null;
}): boolean {
  return agent.runner === "external" && agent.externalExecutionMode === "relay";
}

export function agentHandleFromDisplayName(displayName: string): string {
  return displayName.trim().replace(/\s+/g, "_");
}

export const AgentConfigurationSchema = LightAgentConfigurationSchema.extend({
  instructionsHtml: z.string().nullable(),
  actions: z.array(MCPServerConfigurationSchema),
  skills: z.array(z.string()).optional(),
});

export type AgentConfigurationType = z.infer<typeof AgentConfigurationSchema>;

export type AgentConfigurationWithoutModelType = Omit<
  AgentConfigurationType,
  "model"
>;

export type LightAgentConfigurationWithoutModelType = Omit<
  LightAgentConfigurationType,
  "model"
>;

export interface TemplateAgentConfigurationType {
  name: string;
  pictureUrl: string;

  scope: AgentConfigurationScope;
  description: string;
  model: AgentModelConfigurationType;
  actions: MCPServerConfigurationType[];
  instructions: string | null;
  isTemplate: true;
  maxStepsPerRun?: number;
  tags: TagType[];
}

export const MAX_STEPS_USE_PER_RUN_LIMIT = 64;
// Sub-agents exist to keep the caller's context small: a focused task that runs to the full
// step limit has usually lost its way (one 65-step research run re-read the same 17 files 34
// times and timed its caller out), so they get a tighter cap than the agent that delegated.
export const SUB_AGENT_MAX_STEPS_PER_RUN = 24;
// The chief-of-staff agent scopes, delegates and reports; the work runs in delegates. Its own
// steps cover a build or a short lookup, not research: a 43-step run of its own meant it had
// stopped delegating.
export const ORCHESTRATOR_MAX_STEPS_PER_RUN = 16;
const ACTIONS_PER_STEP_BY_DEPTH = [8, 8, 4, 2] as const;
const MAX_DEPTH_WITH_ACTION_LIMIT = ACTIONS_PER_STEP_BY_DEPTH.length - 1;

// Returns the max actions per step for a given conversation depth.
// Keeps max actions for the first 2 depth levels, then halves: 8 → 8 → 4 → 2,
// capping total concurrent agent loop activities at 512 for a single user message.
export function getMaxActionsPerStep(depth: number): number {
  const normalizedDepth = Number.isFinite(depth) ? Math.trunc(depth) : 0;
  const boundedDepth = Math.max(
    0,
    Math.min(normalizedDepth, MAX_DEPTH_WITH_ACTION_LIMIT)
  );

  return ACTIONS_PER_STEP_BY_DEPTH[boundedDepth];
}

/**
 * Can a user mention a given configuration. `canRead` already carries the
 * requested-space permission check for the acting authenticator, so the status
 * gate is all that remains.
 */
export function canAccessAgent(
  agentConfiguration: Pick<LightAgentConfigurationType, "status" | "canRead">
): boolean {
  switch (agentConfiguration.status) {
    case "active":
    case "draft":
      return agentConfiguration.canRead;
    case "disabled_free_workspace":
    case "disabled_missing_datasource":
    case "disabled_by_admin":
    case "archived":
    case "pending":
      return false;
    default:
      assertNever(agentConfiguration.status);
  }
}

/**
 * Agent events
 */

export const AgentErrorCategories = [
  "retryable_model_error",
  "context_window_exceeded",
  "empty_content",
  "provider_internal_error",
  "stream_error",
  "unknown_error",
  "invalid_response_format_configuration",
  "credits_exhausted",
] as const;

export type AgentErrorCategory = (typeof AgentErrorCategories)[number];

export function isAgentErrorCategory(
  category: unknown
): category is AgentErrorCategory {
  return AgentErrorCategories.includes(category as AgentErrorCategory);
}

// Generic type for the content of an agent / tool error.
export type GenericErrorContent = {
  code: string;
  message: string;
  metadata: Record<string, string | number | boolean> | null;
};

// Generic event sent when an error occurred during the model call.
export type AgentErrorEvent = {
  type: "agent_error";
  created: number;
  configurationId: string;
  messageId: string;
  error: GenericErrorContent;
  runIds?: string[];
};

// Generic event sent when an agent message is done (could be successful, failed, or cancelled).
export type AgentMessageDoneEvent = {
  type: "agent_message_done";
  created: number;
  conversationId: string;
  configurationId: string;
  messageId: string;
  status: "success" | "error";
  // Sibling agents can still be answering the same user message when this one ends.
  isConversationRunningAgentLoop: boolean;
};

// Event sent when an error occurred during the tool call.
export type ToolErrorEvent = {
  type: "tool_error";
  created: number;
  configurationId: string;
  messageId: string;
  conversationId: string;
  error: GenericErrorContent;
  isLastBlockingEventForStep: boolean;
  // TODO(DURABLE-AGENTS 2025-08-25): Move to a deferred event base interface.
  metadata?: {
    pubsubMessageId?: string;
  };
};

export type AgentToolCallStartedEvent = {
  type: "tool_call_started";
  created: number;
  configurationId: string;
  messageId: string;
  toolCallId?: string;
  toolCallIndex?: number;
  toolName: string;
};

// Event sent once the action is completed, we're moving to generating a message if applicable.
export type AgentActionSuccessEvent = {
  type: "agent_action_success";
  created: number;
  configurationId: string;
  messageId: string;
  action: AgentMCPActionWithOutputType;
};

export type GenerativeUiPartCompleteEvent = {
  type: "generative_ui_part_complete";
  created: number;
  configurationId: string;
  messageId: string;
  part: GenerativeUiPart;
};

// Event sent to stop the generation.
export type AgentGenerationCancelledEvent = {
  type: "agent_generation_cancelled";
  created: number;
  configurationId: string;
  messageId: string;
  status: "cancelled" | "interrupted";
};

// Server-rendered view of a finished agent message (the displayed body, chain of
// thought, and activity steps). Computed from the persisted step contents (the
// same source reload uses), so the client can trust it wholesale at stream end
// instead of reconciling its incrementally-built view.
export type AgentMessageContentView = {
  content: string | null;
  chainOfThought: string | null;
  activitySteps: InlineActivityStep[];
};

// Event sent when the agent loop was gracefully stopped (current step completed, then exited).
export type AgentMessageGracefullyStoppedEvent = {
  type: "agent_message_gracefully_stopped";
  created: number;
  configurationId: string;
  messageId: string;
  message: AgentMessageType;
  // Optional: absent on events from an older server during a deploy window.
  contentView?: AgentMessageContentView;
  runIds: string[];
};

// Event sent once the message is completed and successful.
export type AgentMessageSuccessEvent = {
  type: "agent_message_success";
  created: number;
  configurationId: string;
  messageId: string;
  message: AgentMessageType;
  // Optional: absent on events from an older server during a deploy window.
  contentView?: AgentMessageContentView;
  runIds: string[];
};

export type AgentActionsEvent = {
  type: "agent_actions";
  created: number;
  runId: string;
  actions: Array<{
    action: MCPToolConfigurationType;
    functionCallId: string;
  }>;
};

export type AgentContextPrunedEvent = {
  type: "agent_context_pruned";
  created: number;
  configurationId: string;
  messageId: string;
};
